import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, RefreshCw, FileSearch } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Signal { id: string; kind: string; severity: "info" | "warn" | "fail"; title: string; detail?: string; article_id?: string }

/** Static-rule SEO intelligence: scans published articles in-app and surfaces
 *  cannibalization / refresh / weak-link signals without needing GSC sync. */
const SeoIntelTab = () => {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);

  const compute = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_articles")
      .select("id, title, slug, target_keywords, related_slugs, last_refreshed_at, updated_at, status, published")
      .eq("published", true);
    setLoading(false);
    if (error) return;
    const articles = data ?? [];
    const out: Signal[] = [];

    // Cannibalization: same primary keyword on >1 article
    const kwMap = new Map<string, string[]>();
    for (const a of articles) {
      const kw = (a.target_keywords as string[] | null)?.[0]?.toLowerCase();
      if (!kw) continue;
      kwMap.set(kw, [...(kwMap.get(kw) ?? []), a.title as string]);
    }
    for (const [kw, titles] of kwMap) {
      if (titles.length > 1) {
        out.push({
          id: `cannibal-${kw}`, kind: "cannibalization", severity: "warn",
          title: `Cannibalization on "${kw}"`,
          detail: `${titles.length} articles target this keyword: ${titles.join(" · ")}`,
        });
      }
    }

    // Refresh needed: not refreshed in 180+ days
    const cutoff = Date.now() - 180 * 86400000;
    for (const a of articles) {
      const last = new Date((a.last_refreshed_at as string) ?? (a.updated_at as string)).getTime();
      if (last < cutoff) {
        out.push({
          id: `refresh-${a.id}`, kind: "refresh", severity: "warn",
          title: `Refresh recommended: ${a.title}`,
          detail: `Last touched ${Math.round((Date.now() - last) / 86400000)} days ago`,
          article_id: a.id as string,
        });
      }
    }

    // Weak internal linking: published article with < 2 related_slugs
    for (const a of articles) {
      const links = (a.related_slugs as string[] | null) ?? [];
      if (links.length < 2) {
        out.push({
          id: `links-${a.id}`, kind: "internal_links", severity: "info",
          title: `Add internal links: ${a.title}`,
          detail: `Only ${links.length} internal link${links.length === 1 ? "" : "s"}, aim for 2, 4.`,
          article_id: a.id as string,
        });
      }
    }

    // Missing target keywords
    for (const a of articles) {
      const kws = (a.target_keywords as string[] | null) ?? [];
      if (kws.length === 0) {
        out.push({
          id: `kw-${a.id}`, kind: "missing_keywords", severity: "fail",
          title: `No target keywords: ${a.title}`,
          detail: "Set primary + secondary target keywords for SEO tracking.",
          article_id: a.id as string,
        });
      }
    }

    setSignals(out);
  };

  useEffect(() => { compute(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold" style={{ color: "hsl(0 0% 90%)" }}>Operational signals</h3>
          <p className="text-xs mt-0.5" style={{ color: "hsl(0 0% 55%)" }}>
            Detects cannibalization, weak internal linking, stale content, and missing keywords across published articles.
          </p>
        </div>
        <button onClick={compute} className="flex items-center gap-1 text-xs rounded-lg border px-3 py-1.5"
          style={{ background: "transparent", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 80%)" }}>
          <RefreshCw size={12} /> Rescan
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={16} className="animate-spin mr-2" /> Scanning…
        </div>
      ) : signals.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <FileSearch size={28} className="mx-auto mb-3" style={{ color: "hsl(142 50% 55%)" }} />
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>All clear</p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>No SEO operational issues detected on published content.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {signals.map((s) => {
            const color = s.severity === "fail" ? "hsl(0 75% 60%)" : s.severity === "warn" ? "hsl(38 92% 60%)" : "hsl(207 68% 60%)";
            return (
              <li key={s.id} className="flex items-start gap-3 rounded-xl border p-4"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                <AlertTriangle size={14} style={{ color }} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: "hsl(0 0% 90%)" }}>{s.title}</div>
                  {s.detail && <div className="text-xs mt-1" style={{ color: "hsl(0 0% 60%)" }}>{s.detail}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5"
                  style={{ background: "hsl(0 0% 12%)", color }}>{s.kind}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SeoIntelTab;
